import { Flex, GridItem, useToast } from "@chakra-ui/react";
import { FiltersSection } from "./components";
import { useEffect, useState } from "react";
import { useGetDimensions, useGetDimensionsHierarchy } from "../../../../../hooks/query/dimensions";
import { dimensionsKeys, showErrorToast } from "../../../../../utils";
import {
  useExportScopeOneAndTwoDimensionsData
} from "../../../../../hooks/query/reports";
import { hideAppSpinner, showAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { getLowestFilteredDimensionKey, getNextLowestFilteredDimensionKey } from "./components/helpers";
import DimensionReport from "./components/DimensionReport";
import { Card } from "../../../../../components/Card";

function ByDimensionReport({ allReportsData, locationElectricityItems, organizationStructure }) {
  const [initialDimensionsData, setInitialDimensionsData] = useState({});
  const [filtersData, setFiltersData] = useState({});
  const [dimensionsData, setDimensionsData] = useState();
  const toast = useToast();
  const dispatch = useDispatch();

  useEffect(() => {
    if (filtersData && Object.keys(filtersData).length === 0 &&
      initialDimensionsData && Object.keys(initialDimensionsData).length > 0) {
      setDimensionsData({
        ...initialDimensionsData,
        reportId: allReportsData.id
      });
    }
  }, [filtersData, setDimensionsData, initialDimensionsData, allReportsData.id]);

  const handleRemoveFilter = (dimensionKey, dimensionItem) => {
    const newFiltersData = {
      ...filtersData
    };

    const lowestFilteredDimensionKey = getLowestFilteredDimensionKey(filtersData);
    if (dimensionsKeys.indexOf(dimensionKey) < dimensionsKeys.indexOf(lowestFilteredDimensionKey)) {
      // If changes are made to the filter at a level higher than the lowest filtered dimension,
      // we remove all applied filters for dimensions on level below the currently selected dimension
      // in order to prevent data inconsistencies
      dimensionsKeys.forEach(key => {
        if (dimensionsKeys.indexOf(key) > dimensionsKeys.indexOf(dimensionKey)) {
          delete newFiltersData[key];
        }
      });
    }

    if (newFiltersData[dimensionKey] &&
      typeof newFiltersData[dimensionKey].items.find(el => el.id === dimensionItem.id) !== "undefined") {
      if (newFiltersData[dimensionKey].items.length === 1) {
        delete newFiltersData[dimensionKey];
      } else {
        newFiltersData[dimensionKey].items = newFiltersData[dimensionKey].items.filter(el => el.id !== dimensionItem.id);
      }
    }

    setFiltersData(newFiltersData);
  };

  useGetDimensions({},
    {
      onSuccess: data => setInitialDimensionsData(data),
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  const getHierarchyParams = () => {
    // useGetDimensionsHierarchy should only get IDs that are from the same dimension and
    // are from the lowest dimension for which there is filtering
    const hierarchyParams = {
      dimension: dimensionsKeys[0],
      queryParams: ""
    };

    if (Object.keys(filtersData).length > 0) {
      let queryParams = "";
      const lowestFilteredDimensionKey = getLowestFilteredDimensionKey(filtersData);

      filtersData[lowestFilteredDimensionKey].items.forEach((item, itemIndex) => {
        queryParams += `${itemIndex === 0 ? "" : "&"}id=${item.id}`;
      });

      hierarchyParams["dimension"] = lowestFilteredDimensionKey;
      hierarchyParams["queryParams"] = queryParams;
    }

    return hierarchyParams;
  };

  useGetDimensionsHierarchy(getHierarchyParams().queryParams,
    {
      onSuccess: data => {
        const hierarchyDimensionIndex = dimensionsKeys.indexOf(getHierarchyParams().dimension);
        const newDimensionsData = {
          ...initialDimensionsData,
          reportId: allReportsData.id
        };

        if (hierarchyDimensionIndex === 0) {
          for (let index = 1; index < dimensionsKeys.length; index++) {
            newDimensionsData[dimensionsKeys[index]] = data[dimensionsKeys[index]] ?? {
              ...initialDimensionsData[dimensionsKeys[index]],
              items: []
            };
          }
        } else {
          for (let index = 1; index <= hierarchyDimensionIndex; index++) {
            newDimensionsData[dimensionsKeys[index]] = dimensionsData[dimensionsKeys[index]];
          }
          if (hierarchyDimensionIndex < dimensionsKeys.length - 1) {
            // If it's not the last dimension
            for (let index = hierarchyDimensionIndex + 1; index < dimensionsKeys.length; index++) {
              newDimensionsData[dimensionsKeys[index]] = data[dimensionsKeys[index]] ?? {
                ...initialDimensionsData[dimensionsKeys[index]],
                items: []
              };
            }
          }
        }

        setDimensionsData(newDimensionsData);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
      enabled: filtersData && Object.keys(filtersData).length > 0 && getHierarchyParams().queryParams !== ""
    });

  const handleFilterUpdate = (dimensionKey, dimensionObj, itemsIds) => {
    const newFiltersData = {
      ...filtersData
    };

    const lowestFilteredDimensionKey = getLowestFilteredDimensionKey(filtersData);
    if (dimensionsKeys.indexOf(dimensionKey) < dimensionsKeys.indexOf(lowestFilteredDimensionKey)) {
      // If changes are made to the filter at a level higher than the lowest filtered dimension,
      // we remove all applied filters for dimensions on level below the currently selected dimension
      // in order to prevent data inconsistencies
      dimensionsKeys.forEach(key => {
        if (dimensionsKeys.indexOf(key) > dimensionsKeys.indexOf(dimensionKey)) {
          delete newFiltersData[key];
        }
      });
    }

    if (itemsIds && itemsIds.length > 0) {
      newFiltersData[dimensionKey] = {
        dimensionName: dimensionObj.name,
        dimensionId: dimensionObj.id,
        items: dimensionObj.items.filter(item => itemsIds.includes(item.id))
      };
    } else {
      delete newFiltersData[dimensionKey];
    }

    setFiltersData(newFiltersData);
  };

  const constructExportQueryParams = () => {
    const requestParams = {};

    let lowestQueryParams = "";
    let highQueryParams = "";
    if (filtersData && Object.keys(filtersData).length > 0) {
      const lowestFilteredDimensionKey = getLowestFilteredDimensionKey(filtersData);
      filtersData[lowestFilteredDimensionKey].items.forEach((item, itemIndex) => {
        lowestQueryParams += `${itemIndex === 0 ? "" : "&"}${lowestFilteredDimensionKey}=${item.id}`;
      });
      requestParams[lowestFilteredDimensionKey] = lowestQueryParams;
      const higherLevelFilteredDimensionKey = getNextLowestFilteredDimensionKey(filtersData);
      if (higherLevelFilteredDimensionKey) {
        filtersData[higherLevelFilteredDimensionKey].items.forEach((item, itemIndex) => {
          highQueryParams += `${itemIndex === 0 ? "" : "&"}${higherLevelFilteredDimensionKey}=${item.id}`;
        });
        requestParams[higherLevelFilteredDimensionKey] = highQueryParams;
      }
    }

    return Object.values(requestParams).join('&');
  };

  const {
    mutate: exportDimensionsData
  } = useExportScopeOneAndTwoDimensionsData(allReportsData.id, constructExportQueryParams(), {
    onError: err => {
      const parsedErrorObj = JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)));
      showErrorToast(toast, parsedErrorObj?.message ?? err.message);
      dispatch(hideAppSpinner());
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const handleExportClick = () => {
    dispatch(showAppSpinner());
    exportDimensionsData();
  };

  return (
    <Card boxShadow="xs">
      <Flex flexFlow={"column"} gap={4}>
        <GridItem>
          <FiltersSection
            filtersData={filtersData}
            removeFilter={handleRemoveFilter}
            clearAll={() => setFiltersData({})}
            handleExportClick={handleExportClick}
          />
        </GridItem>
        <GridItem>
          {dimensionsData && <DimensionReport
            dimensionsData={dimensionsData}
            locationElectricityItems={locationElectricityItems}
            organizationStructure={organizationStructure}
            updateFilter={handleFilterUpdate}
            filtersData={filtersData}
          />}
        </GridItem>
      </Flex>
    </Card>
  );
}

export default ByDimensionReport;